import React from 'react';
import Icon from '../shared/components/Icon';
import { Link } from '../../features/ui';
import ios from './footer-ios.svg';
import android from './footer-android.svg';
import { useT } from '@spotify-internal/i18n-core';
import { useFooterLabels } from '../../features/Layout/Footer/useFooterLabels';

const year = new Date().getFullYear();

const TRACK_CAT = 'Footer';

export default function Footer() {
  const t = useT();
  const footerLabels = useFooterLabels();
  return (
    <footer className="footer footer-default" role="contentinfo">
      <div className="container">
        <nav
          className="row"
          aria-label={t(
            'FRODOR_FOOTER_LINKS',
            'Links to Spotify resources',
            'collection of links in the footer',
          )}
        >
          <div className="col-xs-12 col-md-2">
            <div className="footer-logo">
              <Link
                external
                href="https://www.spotify.com"
                track={[TRACK_CAT, 'Logo']}
              >
                Spotify
              </Link>
            </div>
          </div>
          <div className="col-xs-12 col-sm-4 col-md-2">
            <h3 className="nav-title">
              {t('FRODOR_0aeec8', 'Helpful Links', 'Link refers to web link')}
            </h3>
            <ul className="nav">
              <li>
                <Link
                  external
                  href="https://www.spotify.com/about-us/contact/"
                  track={[TRACK_CAT, 'About']}
                >
                  {t('FRODOR_2b48af', 'About', '')}
                </Link>
              </li>
              <li>
                <Link
                  external
                  href="https://newsroom.spotify.com"
                  track={[TRACK_CAT, 'Press & Media']}
                >
                  {t(
                    'FRODOR_f24f14',
                    'Press & Media',
                    'Press means newspapers or journalists viewed collectively',
                  )}
                </Link>
              </li>
              <li>
                <Link
                  href="/contact"
                  data-testid="footer-contact"
                  track={[TRACK_CAT, 'Contact Us']}
                  prefetch={false}
                >
                  {t('FRODOR_51f8e4', 'Contact Us', '')}
                </Link>
              </li>
            </ul>
          </div>
          <div className="col-xs-12 col-sm-4 col-md-2">
            <h3 className="nav-title">
              {t('FRODOR_914d07', 'Creator Tools', '')}
            </h3>
            <ul className="nav">
              <li>
                <Link
                  external
                  href="https://app.adjust.com/iya18ah?redirect=https%3A%2F%2Fanchor.fm%2Fapp"
                  track={[TRACK_CAT, 'Creator Tools - Anchor']}
                >
                  Anchor
                </Link>
              </li>
              <li>
                <Link
                  external
                  href="https://www.soundtrap.com/?utm_source=spotify_site&utm_medium=footer_menulink&utm_campaign=alwayson_global_soundtrap_destination"
                  track={[TRACK_CAT, 'Creator Tools - Soundtrap']}
                >
                  Soundtrap
                </Link>
              </li>
              <li>
                <Link
                  external
                  href="https://podcasters.spotify.com/?utm_source=artists.spotify.com"
                  track={[TRACK_CAT, 'Creator Tools - Spotify for Podcasters']}
                >
                  Spotify for Podcasters
                </Link>
              </li>
              <li>
                <Link
                  external
                  href="https://noteable.spotify.com/?utm_source=artists.spotify.com"
                  track={[TRACK_CAT, 'Creator Tools - Noteable']}
                >
                  Noteable
                </Link>
              </li>
            </ul>
          </div>
          <div className="col-xs-12 col-sm-4">
            <h3 className="nav-title">
              {t('FRODOR_8cca0b', 'Get the app', '')}
            </h3>
            <Link
              external
              href="https://itunes.apple.com/us/app/spotify-for-artists/id1222021797"
              track={[TRACK_CAT, 'iOS App']}
            >
              <img
                style={{ width: '170px' }}
                src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
                data-src={ios}
                className="lazyload"
                alt={footerLabels.appStore}
              />
            </Link>
            <Link
              external
              href="https://play.google.com/store/apps/details?id=com.spotify.s4a"
              track={[TRACK_CAT, 'Andriod App']}
            >
              <img
                style={{ width: '170px', marginTop: '12px' }}
                src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
                data-src={android}
                className="lazyload"
                alt={footerLabels.googlePlay}
              />
            </Link>
          </div>
          <div className="col-xs-12 col-md-2 col-social">
            <ul className="nav">
              <li>
                <Link
                  external
                  href="https://twitter.com/spotifyartists"
                  track={[TRACK_CAT, 'Social - Twitter']}
                >
                  <Icon name="twitter-icon" className="social-icon" />
                  <span className="social-text">Twitter</span>
                </Link>
              </li>
              <li>
                <Link
                  external
                  href="https://www.instagram.com/spotifyforartists/"
                  track={[TRACK_CAT, 'Social - Instagram']}
                >
                  <Icon name="instagram-icon" className="social-icon" />
                  <span className="social-text">Instagram</span>
                </Link>
              </li>
            </ul>
          </div>
        </nav>

        <nav
          className="row row-small"
          aria-label={t(
            'FRODOR_FOOTER_LEGAL',
            'Links to Spotify legal resources',
            'collection of links in the footer',
          )}
        >
          <div className="col-xs-7 col-md-6 col-md-offset-2">
            <ul className="nav nav-small">
              <li>
                <Link
                  external
                  href="https://www.spotify.com/legal/spotify-for-artists-terms-and-conditions/"
                  track={[TRACK_CAT, 'Legal']}
                >
                  {t(
                    'FRODOR_LEGAL_ROOT_FOOTER',
                    'Legal',
                    'Legal refers to terms and conditions',
                  )}
                </Link>
              </li>
              <li>
                <Link
                  external
                  href="https://www.spotify.com/legal/privacy-policy/"
                  track={[TRACK_CAT, 'Privacy Policy']}
                >
                  {t('FRODOR_a92e8f', 'Privacy Policy', '')}
                </Link>
              </li>
              <li>
                <Link
                  external
                  href="https://www.spotify.com/legal/cookies-policy/"
                  track={[TRACK_CAT, 'Cookies']}
                >
                  {t(
                    'FRODOR_81cd99',
                    'Cookies',
                    'Cookies are a user tracking method for the web',
                  )}
                </Link>
              </li>
            </ul>
          </div>
          <div className="col-xs-5 col-md-4 text-right">
            <small className="copyright">&copy; {year} Spotify AB</small>
          </div>
        </nav>
      </div>
    </footer>
  );
}
